import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  PageHeader,
  StatusBadge,
  ApproverRoleBadge,
  EmptyState,
} from "./shared/components";
import {
  getHrFinalReviewList,
  HrFinalReviewRow,
} from "./services/eSeparation.service";

type TabKey = "pending" | "processed";

// A resignation is Processed only once HR has recorded its own final decision.
// Status alone is not enough: the request can sit at CA_APPROVED while still
// awaiting HR, or be REJECTED by the CA without HR having signed off.
const isProcessed = (item: HrFinalReviewRow) => !!item.hr_action;

const NoDuesHRReview: React.FC = () => {
  const navigate = useNavigate();
  const [cases, setCases] = useState<HrFinalReviewRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState<TabKey>("pending");

  const load = () => {
    setLoading(true);
    getHrFinalReviewList()
      .then((list) => setCases(Array.isArray(list) ? list : []))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const inTab = (item: HrFinalReviewRow) =>
    tab === "pending" ? !isProcessed(item) : isProcessed(item);

  const processedCount = cases.filter(isProcessed).length;
  const pendingCount = cases.length - processedCount;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return cases.filter((item) => {
      if (!inTab(item)) return false;
      if (!q) return true;
      const name = String(item.emp_name || "").toLowerCase();
      const empNo = String(item.emp_no || "");
      const reqId = String(item.request_id || "").toLowerCase();
      return name.includes(q) || empNo.includes(search) || reqId.includes(q);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cases, search, tab]);

  const openDetail = (item: HrFinalReviewRow) => {
    navigate(`/e-sepration/no-dues/hr-review/${item.request_id}`, {
      state: { request_id: item.request_id, emp_no: item.emp_no, role: "HR" },
    });
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <PageHeader
          title="HR Final Review & Sign-off"
          subtitle="Tap a case to review consolidated clearance & sign-off"
          right={<ApproverRoleBadge role="HR" label="HR Admin" />}
          onExit={() => navigate(-1)}
        />

        {/* Pending / Processed tabs */}
        <div
          className="d-flex p-1 mb-3"
          style={{ background: "rgba(0,0,0,0.04)", borderRadius: 10 }}
        >
          {[
            { key: "pending", label: `Pending (${pendingCount})` },
            { key: "processed", label: `Processed (${processedCount})` },
          ].map((t) => (
            <button
              key={t.key}
              className="btn flex-fill fw-semibold"
              style={{
                borderRadius: 8,
                background: tab === t.key ? "#fff" : "transparent",
                color: tab === t.key ? "#BA7517" : "#6c757d",
                boxShadow: tab === t.key ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
              }}
              onClick={() => setTab(t.key as TabKey)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Search + count */}
        <div className="card border-0 shadow-sm mb-3" style={{ borderRadius: 12 }}>
          <div className="card-body d-flex flex-wrap align-items-center gap-2">
            <div className="input-icon flex-grow-1" style={{ minWidth: 240 }}>
              <span className="input-icon-addon">
                <i className="ti ti-search" />
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Search by name, employee no. or request ID"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <span className="badge bg-warning-transparent text-warning fs-12 px-3 py-2">
              {filtered.length} {tab === "pending" ? "pending" : "processed"}
            </span>
            <button className="btn btn-outline-secondary" onClick={load} title="Refresh">
              <i className="ti ti-refresh" />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-warning" role="status" />
            <p className="text-muted mt-3 mb-0">Loading cases...</p>
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon="ti-file-stack"
            title={tab === "pending" ? "No Cases Pending Review" : "No Processed Cases"}
            message={
              tab === "pending"
                ? "No-Dues cases awaiting your final sign-off appear here. Use refresh to check for new cases."
                : "No-Dues cases you have approved or rejected appear here."
            }
          />
        ) : (
          <div className="row g-3">
            {filtered.map((item) => (
              <div className="col-md-6 col-xl-4" key={item.request_id}>
                <div
                  className="card border-0 shadow-sm h-100"
                  style={{
                    borderRadius: 14,
                    borderLeft: "4px solid #BA7517",
                    cursor: "pointer",
                  }}
                  onClick={() => openDetail(item)}
                >
                  <div className="card-body">
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <span
                        className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: 11,
                          background: "#FFF1DE",
                          color: "#BA7517",
                        }}
                      >
                        <i className="ti ti-shield-check" style={{ fontSize: 18 }} />
                      </span>
                      <div className="flex-grow-1 overflow-hidden">
                        <div className="fw-semibold text-truncate">
                          {item.emp_name || "—"}
                        </div>
                        <small className="text-muted text-truncate d-block">
                          {item.emp_no} • {item.designation || "—"}
                        </small>
                      </div>
                      <StatusBadge status={item.status || "PENDING_APPROVAL"} />
                    </div>

                    <div className="d-flex align-items-center justify-content-between border-top pt-2">
                      <span className="text-muted small d-inline-flex align-items-center gap-1">
                        <i className="ti ti-tag" />
                        {item.request_id}
                      </span>
                      <span className="text-muted small d-inline-flex align-items-center gap-1">
                        <i className="ti ti-arrow-forward-up" />
                        {tab === "pending" ? "Awaiting sign-off" : "Finalized"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NoDuesHRReview;
